import { useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { toast } from 'react-toastify';
import { DepositBehavior } from '@prisma/client';
import FormSmallModal, { InputRow } from '../global/FormSmallModal';
import useBusiness from '../../hooks/businesses/useBusiness';
import useConfirm from '../../hooks/useConfirm';
import {
  flashbookSettingsSchema,
  FlashbookSettingsPayload,
} from '../../types/flashbooks';
import {
  FLASHBOOK_SETTINGS_DEFAULTS,
  FLASH_DESIGN_DEPOSIT_OPTIONS,
} from '../../constants/flashbooks';
import { getShopFlashbookUrl } from '../../helpers/flashbooks';
import useUpdateFlashbook from '../../hooks/flashbooks/useUpdateFlashbook';

interface Props {
  open: boolean;
  onClose: () => void;
  flashbookId: string | undefined;
  initialValues?: Partial<FlashbookSettingsPayload>;
}

export default function FlashbookSettingsFormModal({
  open,
  onClose,
  flashbookId,
  initialValues: passedInitial,
}: Props) {
  const { business } = useBusiness();
  const confirm = useConfirm();
  const { mutateAsync: updateFlashbook } = useUpdateFlashbook();

  const initialValues = useMemo<FlashbookSettingsPayload>(
    () => ({
      enabled: passedInitial?.enabled ?? true,
      enableDirectBookings:
        passedInitial?.enableDirectBookings ??
        FLASHBOOK_SETTINGS_DEFAULTS.enableDirectBookings,
      depositBehavior:
        passedInitial?.depositBehavior ??
        FLASHBOOK_SETTINGS_DEFAULTS.depositBehavior,
      depositAmount:
        passedInitial?.depositAmount &&
        Number.parseFloat(String(passedInitial.depositAmount)) > 0
          ? String(passedInitial.depositAmount)
          : '',
      depositPercentage:
        passedInitial?.depositPercentage &&
        Number.parseFloat(String(passedInitial.depositPercentage)) > 0
          ? String(passedInitial.depositPercentage)
          : '',
      applyToAllDesigns: false,
    }),
    [passedInitial],
  );

  const inputRows = useMemo<InputRow<FlashbookSettingsPayload>[]>(
    () => [
      [
        {
          id: 'shopFlashLink',
          inputType: 'copy',
          label: 'Storefront link:',
          value: getShopFlashbookUrl(business),
          name: 'shopFlashLink' as never,
          footerAction: getShopFlashbookUrl(business)
            ? {
                label: 'Preview',
                onClick: () => {
                  const url = getShopFlashbookUrl(business);
                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                },
              }
            : undefined,
        },
      ],
      [
        {
          inputType: 'switch',
          label: 'Show Shop Flash on your storefront',
          name: 'enabled',
        },
      ],
      [
        {
          inputType: 'switch',
          label: 'Enable direct bookings',
          description:
            'Clients can schedule flash directly without needing approval.',
          name: 'enableDirectBookings',
        },
      ],
      [
        {
          inputType: 'radio-select',
          label: 'Payment rule',
          name: 'depositBehavior',
          options: FLASH_DESIGN_DEPOSIT_OPTIONS.map(option => ({
            label: option.label,
            value: option.value,
          })),
        },
      ],
      [
        {
          inputType: 'text',
          label: 'Deposit amount',
          name: 'depositAmount',
          placeholder: '50',
          isVisible: (vals: Record<string, unknown>) =>
            vals.depositBehavior === 'FLAT_FEE',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'Deposit percentage',
          name: 'depositPercentage',
          placeholder: '25',
          suffix: '%',
          isVisible: (vals: Record<string, unknown>) =>
            vals.depositBehavior === 'PERCENTAGE',
        },
      ],
    ],
    [business],
  );

  return (
    <FormSmallModal
      open={open}
      onClose={onClose}
      allowUntouched={false}
      form={{
        title: 'Shop Flash Settings',
        initialValues,
        inputRows,
        onSubmit: async values => {
          if (!flashbookId) return;

          const designSettingsChanged = !isEqual(
            {
              enableDirectBookings: values.enableDirectBookings,
              depositBehavior: values.depositBehavior,
              depositAmount: values.depositAmount,
              depositPercentage: values.depositPercentage,
            },
            {
              enableDirectBookings: initialValues.enableDirectBookings,
              depositBehavior: initialValues.depositBehavior,
              depositAmount: initialValues.depositAmount,
              depositPercentage: initialValues.depositPercentage,
            },
          );
          const enabledChanged = values.enabled !== initialValues.enabled;

          if (!designSettingsChanged && !enabledChanged) {
            onClose();
            return;
          }

          const applyToAllDesigns = designSettingsChanged
            ? await confirm({
                title: 'Apply to all designs?',
                message:
                  'These settings can override the booking and payment configuration on every existing flash design. Apply to all?',
                positiveText: 'Apply to all',
                negativeText: 'Only update defaults',
                dismissible: false,
                hideCloseButton: true,
              })
            : false;

          const parseAmount = (v: string) =>
            Number.parseFloat(v.replace(/[^0-9.]/g, '') || '0');

          const behavior = values.depositBehavior as DepositBehavior;

          await updateFlashbook({
            flashbookId,
            payload: {
              enabled: values.enabled,
              enableDirectBookings: values.enableDirectBookings,
              depositBehavior: behavior,
              depositAmount:
                behavior === 'FLAT_FEE'
                  ? parseAmount(values.depositAmount ?? '')
                  : 0,
              depositPercentage:
                behavior === 'PERCENTAGE'
                  ? parseAmount(values.depositPercentage ?? '') / 100
                  : 0,
              applyToAllDesigns,
            },
          });
          toast.success('Settings saved');
          onClose();
        },
        validationSchema: flashbookSettingsSchema,
        submitButtonText: 'Save',
      }}
      maxWidth={600}
    />
  );
}
